import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Glasses,
  Key,
  Globe,
  Code,
  LogOut,
  Menu,
  X,
  ChevronRight,
  BarChart3,
  Crown,
  CreditCard,
  User,
  Languages,
  FileText,
} from 'lucide-react';
import { auth, admin } from './api';
import visionLiveLogo from '../assets/images/visionlive.png';

const baseNavItems = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'products', label: 'Products', icon: Glasses },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'subscription', label: 'Subscription', icon: CreditCard },
  { id: 'api-key', label: 'API Key', icon: Key },
  { id: 'domains', label: 'Domain', icon: Globe },
  { id: 'widget', label: 'Language', icon: Languages },
  { id: 'implementation', label: 'Implementation', icon: Code },
  { id: 'account', label: 'Account', icon: User },
];

const adminNavItem = { id: 'admin', label: 'Admin', icon: Crown, adminOnly: true };
const blogNavItem = { id: 'blog', label: 'Blog', icon: FileText, adminOnly: true };

const DashboardLayout = ({ children, currentPage, onNavigate, user }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  
  // Build nav items: for superadmin hide Account & Subscription, add Blog + Admin
  const navItems = isSuperAdmin
    ? [
        ...baseNavItems.filter((item) => item.id !== 'account' && item.id !== 'subscription'),
        blogNavItem,
        adminNavItem,
      ]
    : baseNavItems;

  // Check for admin access
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const { isSuperAdmin: isAdmin } = await admin.checkAccess();
        setIsSuperAdmin(isAdmin);
      } catch {
        setIsSuperAdmin(false);
      }
    };
    checkAdmin();
  }, []);

  const handleSignOut = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error('Sign out failed:', error);
    }
    
    // Clear all better-auth cookies manually as fallback
    // This handles cookies set before cross-subdomain fix
    const cookieNames = [
      'better-auth.session_token',
      'better-auth.session_data', 
      '__session',
      'session'
    ];
    const domains = ['', '.vltryon.com', 'vltryon.com', 'api.vltryon.com'];
    const paths = ['/', ''];
    
    cookieNames.forEach(name => {
      domains.forEach(domain => {
        paths.forEach(path => {
          const domainPart = domain ? `; domain=${domain}` : '';
          const pathPart = path ? `; path=${path}` : '';
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT${domainPart}${pathPart}`;
        });
      });
    });
    
    // Clear any cached session data
    sessionStorage.clear();
    localStorage.clear();
    
    // Force a hard redirect to home with cache bypass
    window.location.replace('/');
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#0d0d0d]/95 backdrop-blur-md border-b border-white/5 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={visionLiveLogo}
              alt="VisionLive"
              className="h-8 w-auto max-w-[160px] object-contain"
            />
          </div>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 z-40 bg-black/80 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ x: -280 }}
            animate={{ x: 0 }}
            exit={{ x: -280 }}
            transition={{ type: 'spring', damping: 25 }}
            className="lg:hidden fixed top-0 left-0 bottom-0 w-[280px] z-50 bg-[#0d0d0d] border-r border-white/5 pt-16"
          >
            <nav className="p-4 space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = currentPage === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      onNavigate(item.id);
                      setMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      isActive
                        ? 'bg-blue-500/10 text-blue-400'
                        : 'text-white/60 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </button>
                );
              })}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop Sidebar */}
      <motion.aside
        initial={false}
        animate={{ width: sidebarOpen ? 260 : 80 }}
        className="hidden lg:flex fixed top-0 left-0 bottom-0 flex-col bg-[#0d0d0d] border-r border-white/5 z-30"
      >
        {/* Logo */}
        <div className="p-4 border-b border-white/5">
          <div className="flex items-center gap-3">
            <img
              src={visionLiveLogo}
              alt="VisionLive"
              className={`h-9 w-auto object-contain ${sidebarOpen ? 'max-w-[170px]' : 'max-w-[44px]'}`}
            />
            {sidebarOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col"
              >
                <span className="text-xs text-blue-400">Dashboard</span>
              </motion.div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentPage === item.id;
            return (
              <button
                key={item.id}
                onClick={() => onNavigate(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                  isActive
                    ? 'bg-blue-500/10 text-blue-400'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="font-medium"
                  >
                    {item.label}
                  </motion.span>
                )}
              </button>
            );
          })}
        </nav>

        {/* User Section */}
        <div className="p-3 border-t border-white/5">
          {user && sidebarOpen && (
            <div className="flex items-center gap-3 px-3 py-2 mb-2">
              {user.image ? (
                <img src={user.image} alt="" className="w-8 h-8 rounded-full" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <span className="text-sm font-medium text-blue-400">
                    {user.name?.charAt(0) || user.email?.charAt(0)}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user.name}</p>
                <p className="text-xs text-white/40 truncate">{user.email}</p>
              </div>
            </div>
          )}
          <button
            onClick={handleSignOut}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-white/60 hover:text-red-400 hover:bg-red-500/10 transition-all"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span className="font-medium">Sign Out</span>}
          </button>
        </div>

        {/* Collapse Toggle */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-[#1a1a1f] border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors"
        >
          <ChevronRight
            className={`w-4 h-4 text-white/60 transition-transform ${
              sidebarOpen ? 'rotate-180' : ''
            }`}
          />
        </button>
      </motion.aside>

      {/* Main Content */}
      <main
        className={`min-h-screen pt-16 lg:pt-0 transition-all ${
          sidebarOpen ? 'lg:pl-[260px]' : 'lg:pl-[80px]'
        }`}
      >
        <div className="p-4 lg:p-8">{children}</div>
      </main>
    </div>
  );
};

export default DashboardLayout;

